import { useEffect, useState } from 'react';
import { WidgetCard } from '@/components/WidgetCard';
import { ArrowDown2, ArrowUp2, Hierarchy } from 'iconsax-reactjs';
import { useSymbol } from '@/lib/useSymbol';
import { cn } from '@/lib/utils';
import { useKnotSummary, visibleRows, type KnotRow, type TakenKnot } from './summary';
import { useI18n } from '@/lib/i18n';
import { timeframeShort } from '@pipyar/core';

/** A knot taken within this is worth calling out as just-happened. */
const FRESH_SECONDS = 30 * 60;

/**
 * The most recently taken knot on each timeframe.
 *
 * Ordered by how recently each was taken rather than by timeframe, so the tile
 * answers "what just happened" instead of listing M1 through W1 in a fixed order
 * where the interesting row could be anywhere.
 */
export function KnotsWidget() {
  const { symbol, isLoading: symbolLoading } = useSymbol();

  const query = useKnotSummary(symbol);
  const { t } = useI18n();

  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    // A minute is enough: these ages are shown to the minute, not the second.
    const id = setInterval(() => setNow(Date.now() / 1000), 30_000);
    return () => clearInterval(id);
  }, []);

  const serverTime = query.data?.serverTime;
  const [skew, setSkew] = useState(0);
  useEffect(() => {
    if (serverTime !== undefined) setSkew(serverTime - Date.now() / 1000);
  }, [serverTime]);

  const clockNow = now + skew;
  const digits = query.data?.digits ?? 2;
  const loading = query.isPending || symbolLoading;

  const rows = visibleRows(query.data);

  const freshest = rows[0];
  const fresh = freshest !== undefined && clockNow - freshest.knot.takenAt <= FRESH_SECONDS;

  return (
    <WidgetCard
      to="/knots"
      icon={<Hierarchy size={16} variant="Bold" />}
      title={t('knots.title')}
      module="knots"
      badge={
          !loading && fresh ? (
            <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
              {t('knots.justTaken')}
            </span>
          ) : undefined
      }
    >

      <div className="p-3">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="h-9 w-full animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">
            {t('knots.noneTaken')}
          </p>
        ) : (
          <ul className="space-y-1">
            {rows.map((r, i) => (
              <Card key={r.timeframe} row={r} digits={digits} age={clockNow - r.knot.takenAt} first={i === 0} />
            ))}
          </ul>
        )}
      </div>
    </WidgetCard>
  );
}

function Card({
  row,
  digits,
  age,
  first,
}: {
  row: KnotRow & { knot: TakenKnot };
  digits: number;
  age: number;
  first: boolean;
}) {
  const { t } = useI18n();
  const { knot } = row;
  /*
   * Coloured by what the take MEANS, not by where price came from.
   *
   * It used to show the approach direction, which reads as the opposite signal
   * half the time: price falling onto a level and closing red there is a
   * bullish exhaustion, and a red arrow said the reverse.
   */
  const bullish = knot.bias === 'bullish';

  return (
    <li
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-2 py-1.5',
        first ? 'bg-primary/8' : 'bg-elevated/40',
      )}
    >
      <span className="w-7 shrink-0 text-[10px] font-semibold text-muted-foreground">
        {timeframeShort(row.timeframe)}
      </span>

      <span
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded',
          bullish ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
        )}
        title={t(bullish ? 'knots.biasBullish' : 'knots.biasBearish')}
      >
        {bullish ? <ArrowUp2 size={12} variant="Bold" /> : <ArrowDown2 size={12} variant="Bold" />}
      </span>

      <span className="tabular min-w-0 flex-1 text-sm font-medium">
        {knot.level.toFixed(digits)}
      </span>

      <span className="shrink-0 text-end">
        <span className="tabular block text-[11px] leading-tight">{formatAge(age)}</span>
        <span className="block text-[10px] leading-tight text-muted-foreground">
          {knot.barsLater} bar{knot.barsLater === 1 ? '' : 's'} later
        </span>
      </span>
    </li>
  );
}

/** "4m", "2h 10m", "3d" — coarser the further back it goes. */
function formatAge(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  if (total < 60) return 'just now';
  const m = Math.floor(total / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h ago`;
}
